import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiPlus, FiFileText, FiChevronLeft, FiChevronRight, FiChevronDown, FiFilter, FiX } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import SearchInput from '../../components/ui/SearchInput';
import StatusPill from '../../components/ui/StatusPill';
import ErrorBanner from '../../components/ui/ErrorBanner';
import EmptyState from '../../components/ui/EmptyState';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import {
  listInvoicesPaginated,
  listOverdueInvoicesPaginated,
  listInvoicesByStatusPaginated,
  listInvoicesFilterPaginated,
  updateInvoiceStatus,
} from '../../api/invoices';
import { extractErrorMessage } from '../../api/client';
import { InvoiceStatus } from '../../enums';
import InvoiceForm from './InvoiceForm';
import InvoiceDetailDrawer from './InvoiceDetailDrawer';

const TABS = [
  { key: 'ALL', label: 'All' },
  { key: InvoiceStatus.DRAFT, label: 'Draft' },
  { key: InvoiceStatus.SENT, label: 'Sent' },
  { key: InvoiceStatus.PARTIAL, label: 'Partial' },
  { key: InvoiceStatus.PAID, label: 'Paid' },
  { key: InvoiceStatus.OVERDUE, label: 'Overdue' },
  { key: InvoiceStatus.CANCELLED, label: 'Cancelled' },
];

function pendingOf(inv) {
  return Math.max(0, Number(inv?.totalAmount || 0) - Number(inv?.paidAmount || 0));
}

export default function InvoicesListPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const qMonth = searchParams.get('month');
  const qYear = searchParams.get('year');

  const [tab, setTab] = useState('ALL');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [tab, search]);

  const isOverdueTab = tab === InvoiceStatus.OVERDUE;

  const fetchInvoices = async ({ queryKey }) => {
    const [_key, qTab, qPage, qSize, month, year] = queryKey;
    if (month && year) {
      const mStr = String(month).padStart(2, '0');
      const start = `${year}-${mStr}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${mStr}-${String(lastDay).padStart(2, '0')}`;
      
      return listInvoicesFilterPaginated({
        invoiceDateFrom: start,
        invoiceDateTo: end,
        page: qPage,
        size: qSize,
        ...(qTab !== 'ALL' && qTab !== InvoiceStatus.OVERDUE && { status: qTab }),
        ...(qTab === InvoiceStatus.OVERDUE && { overdue: true })
      });
    }
    if (qTab === InvoiceStatus.OVERDUE) return listOverdueInvoicesPaginated(qPage, qSize);
    if (qTab !== 'ALL') return listInvoicesByStatusPaginated(qTab, qPage, qSize);
    return listInvoicesPaginated(qPage, qSize);
  };

  const activeQuery = useQuery({
    queryKey: ['invoices', tab, page, pageSize, qMonth, qYear],
    queryFn: fetchInvoices,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => updateInvoiceStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Status updated');
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const pageData = activeQuery.data || {};
  const rawList = pageData.content || (Array.isArray(pageData) ? pageData : []);
  const totalPages = pageData.totalPages || 1;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = rawList;
    if (q) {
      arr = arr.filter((inv) => {
        const num = String(inv.invoiceNumber || '').toLowerCase();
        const cust = String(inv.customerName ?? inv.customerId ?? '').toLowerCase();
        return num.includes(q) || cust.includes(q);
      });
    }
    return arr;
  }, [rawList, search]);

  const columns = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-primary">
            {r.invoiceNumber || `#${r.id}`}
          </span>
        </div>
      ),
    },
    {
      key: 'customerName',
      header: 'Customer',
      render: (r) => (r.customerName ? <span className="text-slate-600">{r.customerName}</span> : (r.customerId != null ? `#${r.customerId}` : '—')),
    },
    {
      key: 'createdAt',
      header: 'Issued',
      render: (r) => <DateDisplay value={r.createdAt} />,
    },
    {
      key: 'dueDate',
      header: 'Due',
      render: (r) => {
        if (!r.dueDate) return '—';
        const start = new Date(r.invoiceDate || r.createdAt);
        start.setHours(0, 0, 0, 0);
        const due = new Date(r.dueDate);
        due.setHours(0, 0, 0, 0);
        const diffTime = due.getTime() - start.getTime();
        const termDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        
        const daysStr = termDays > 0 ? `(${termDays} days)` : '';
        
        return (
          <div className="flex items-center gap-1">
            <DateDisplay value={r.dueDate} />
            {daysStr && (
              <span className="text-[11px] whitespace-nowrap text-slate-500 font-medium">
                {daysStr}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'totalAmount',
      header: 'Total',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.totalAmount} currency={r.currency} />,
    },
    {
      key: 'paidAmount',
      header: 'Paid',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.paidAmount} currency={r.currency} />,
    },
    {
      key: 'pending',
      header: 'Pending',
      align: 'right',
      render: (r) => {
        const p = pendingOf(r);
        return (
          <span className={p > 0 ? 'font-medium text-warning' : 'text-ink-muted'}>
            <CurrencyDisplay value={p} currency={r.currency} />
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div className="relative inline-block w-full max-w-[110px]" onClick={(e) => e.stopPropagation()}>
          <select
            value={r.status}
            onChange={(e) => updateStatusMutation.mutate({ id: r.id, status: e.target.value })}
            disabled={updateStatusMutation.isPending && updateStatusMutation.variables?.id === r.id}
            className="w-full text-xs font-medium pl-3 pr-8 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors cursor-pointer appearance-none"
          >
            {Object.values(InvoiceStatus).map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-500">
            <FiChevronDown size={14} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Invoices" breadcrumb="Finance / Invoices">
      <PageHeader
        title="Invoices"
        description="Create, send, and track customer invoices."
        actions={
          <Button icon={FiPlus} onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-sm">
            New Invoice
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="-mb-px flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setTab(t.key); setPage(0); }}
                className={`relative inline-flex items-center gap-2 rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border border-b-white border-slate-200 bg-white text-primary'
                    : 'text-ink-muted hover:bg-slate-50 hover:text-ink'
                }`}
              >
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {qMonth && qYear && (
             <Button 
               variant="outline" 
               size="sm" 
               icon={FiX}
               onClick={() => {
                 searchParams.delete('month');
                 searchParams.delete('year');
                 setSearchParams(searchParams);
                 setPage(0);
               }}
               className="text-primary whitespace-nowrap"
             >
               Clear {new Date(qYear, qMonth - 1).toLocaleString('default', { month: 'short' })} Filter
             </Button>
          )}
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by invoice # or customer..."
            className="w-full sm:w-80"
          />
        </div>
      </div>

      {activeQuery.error && (
        <div className="mb-4">
          <ErrorBanner
            error={activeQuery.error}
            title="Could not load invoices"
            onRetry={activeQuery.refetch}
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <DataTable
          columns={columns}
          rows={filtered}
          loading={activeQuery.isLoading}
          error={!!activeQuery.error}
          empty={
            <EmptyState
              icon={FiFileText}
              title={
                search
                  ? 'No invoices match your search'
                  : tab === 'ALL'
                  ? 'No invoices yet'
                  : `No ${tab.toLowerCase()} invoices`
              }
              description={
                tab === 'ALL'
                  ? 'Create your first invoice to start billing customers.'
                  : 'Try a different filter or search query.'
              }
              action={
                tab === 'ALL' &&
                !search && (
                  <Button icon={FiPlus} onClick={() => setCreateOpen(true)} variant="outline">
                    New Invoice
                  </Button>
                )
              }
            />
          }
          onRowClick={(row) => setSelected(row)}
        />

        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <span className="text-sm text-slate-500">
                Page Size:
              </span>
              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="w-20 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 mr-2">
                Page <span className="font-medium text-slate-700">{page + 1}</span> of <span className="font-medium text-slate-700">{totalPages}</span>
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                icon={FiChevronLeft} 
                disabled={page === 0} 
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page >= totalPages - 1} 
                onClick={() => setPage(p => p + 1)}
              >
                Next <FiChevronRight className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <InvoiceForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(data) => setSelected(data)}
      />

      <InvoiceDetailDrawer
        open={!!selected}
        invoiceId={selected?.id}
        fallback={selected}
        onClose={() => setSelected(null)}
      />
    </AppLayout>
  );
}
